import React, { useState } from "react";

import HoldingAcquisitionHistory from "./HoldingAcquisitionHistory";
import InvestorExpandable from "./InvestorExpandable";
import FundedProjectMarketCard from "./FundedProjectMarketCard";
import PortfolioHoldingMetrics from "../PortfolioHoldingMetrics";
import PortfolioPieChart from "./PortfolioPieChart";
import ShareBalanceBar from "./ShareBalanceBar";

import { formatProjectCodeDisplay, getProjectLifecycleLabel, projectTitleWithCode } from "../../utils/projectViews";
import { holdingBuyValueTotal, portfolioHoldingPricePerShare, portfolioHoldingValueTotal } from "../../utils/investmentPricing";

function projectKey(id) {
  return id == null ? "" : String(id);
}

function PortfolioPieAllocation({ holdings, valueForHolding, formatMoney }) {
  return (
    <div className="investor-portfolio-both__top">
      <h4 className="investor-portfolio-visualization__heading investor-portfolio-both__heading-chart">
        Allocation (est. value)
      </h4>
      <h4 className="investor-portfolio-visualization__heading investor-portfolio-both__heading-legend">
        By project
      </h4>
      <div className="investor-portfolio-both__chart">
        <PortfolioPieChart
          holdings={holdings}
          valueForHolding={valueForHolding}
          formatMoney={formatMoney}
          layout="chart-only"
        />
      </div>
      <aside className="investor-portfolio-both__legend" aria-label="Allocation by project">
        <PortfolioPieChart
          holdings={holdings}
          valueForHolding={valueForHolding}
          formatMoney={formatMoney}
          layout="legend-only"
        />
      </aside>
    </div>
  );
}

export default function InvestorPortfolioVisualization({
  holdings,
  redemptionBalances,
  formatShare,
  formatMoney,
  lifecyclePillClass,
  /** When true (e.g. Shares Management), pie / combined views are hidden — use dashboard for allocation chart. */
  hidePieChart = false,
  /** Optional per-project price update panel (shown inside each holding toggle). */
  priceByProject = null,
  priceRefreshingId = null,
  onRefreshPrice,
  onSharesCertificate,
}) {
  const [view, setView] = useState(hidePieChart ? "bars" : "both");

  if (!holdings?.length) {
    return <p className="muted">No share holdings yet.</p>;
  }

  const valueForHolding = (h) => portfolioHoldingValueTotal(h, h.project);
  const showPriceUpdate = Boolean(priceByProject);

  const holdingsList = (
    <>
      {holdings.map((h) => {
        const code = formatProjectCodeDisplay(h.project?.project_code) || "—";
        const name = h.project?.name || "—";
        const cost = holdingBuyValueTotal(h);
        const value = valueForHolding(h);
        const pnl = value - cost;
        const pnlFmt = `${pnl >= 0 ? "+" : ""}${formatMoney(pnl)}`;
        const pnlClass =
          pnl > 0.01
            ? "investor-holding-card__pnl--up"
            : pnl < -0.01
              ? "investor-holding-card__pnl--down"
              : "investor-holding-card__pnl--flat";
        const bal = redemptionBalances.find((b) => String(b.project_id) === String(h.project?.id));
        const pid = h.project?.id;
        const pkey = projectKey(pid);
        const sharesHeld = Number(h.shares_held) || 0;
        return (
          <InvestorExpandable
            key={h.id}
            title={projectTitleWithCode(h.project) || `${code}: ${name}`}
            subtitle={`${formatShare(h.shares_held)} shares`}
            badge={
              <span className={lifecyclePillClass(h.project?.status)}>
                {h.project?.status ? getProjectLifecycleLabel(h.project.status) : "—"}
              </span>
            }
          >
            <div className="investor-holding-detail">
              <section className="investor-holding-detail__block investor-holding-detail__block--summary" aria-label="Position summary">
                <PortfolioHoldingMetrics
                  project={h.project}
                  sharesHeld={h.shares_held}
                  codeDisplay={code}
                  projectName={name}
                  sharesFormatted={formatShare(h.shares_held)}
                  costFormatted={formatMoney(cost)}
                  navFormatted={formatMoney(portfolioHoldingPricePerShare(h.project))}
                  valueFormatted={formatMoney(value)}
                  showProjectIdentity={false}
                />
                <p className={`investor-holding-card__pnl ${pnlClass}`}>Unrealised P&L: {pnlFmt}</p>
              </section>

              {bal ? (
                <section className="investor-holding-detail__block investor-holding-detail__block--redemption" aria-label="Redemption">
                  <ShareBalanceBar
                    label="Redemption availability"
                    held={bal.shares_held ?? h.shares_held}
                    locked={bal.shares_pending_redemption}
                    available={bal.shares_available_for_redemption}
                    redeemed={bal.shares_redeemed_total}
                  />
                </section>
              ) : null}

              {showPriceUpdate && h.project ? (
                <section className="investor-holding-detail__block investor-holding-detail__block--market" aria-label="Price update">
                  <div className="investor-holding-card__price-update invx-funded-market-card">
                    <FundedProjectMarketCard
                      project={h.project}
                      priceSeries={(pkey && priceByProject[pkey]) || []}
                      onRefresh={pkey && onRefreshPrice ? () => onRefreshPrice(pid) : undefined}
                      refreshing={priceRefreshingId === pkey}
                      mySharesHeld={sharesHeld}
                      showTradeActions={false}
                      showSupplyMetrics={false}
                      showProjectTitle={false}
                      showProjectVisual={false}
                      onSharesCertificate={
                        onSharesCertificate ? () => onSharesCertificate(h.project) : undefined
                      }
                    />
                  </div>
                </section>
              ) : null}

              <section className="investor-holding-detail__block investor-holding-detail__block--history" aria-label="Acquisition history">
                <HoldingAcquisitionHistory
                  projectId={h.project?.id}
                  formatShare={formatShare}
                  formatMoney={formatMoney}
                />
              </section>
            </div>
          </InvestorExpandable>
        );
      })}
    </>
  );

  return (
    <>
      {!hidePieChart ? (
        <div className="investor-portfolio-view-tabs" role="tablist" aria-label="Portfolio view">
          <button
            type="button"
            role="tab"
            aria-selected={view === "both"}
            className={`investor-portfolio-view-tabs__btn${view === "both" ? " investor-portfolio-view-tabs__btn--active" : ""}`}
            onClick={() => setView("both")}
          >
            Both
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "bars"}
            className={`investor-portfolio-view-tabs__btn${view === "bars" ? " investor-portfolio-view-tabs__btn--active" : ""}`}
            onClick={() => setView("bars")}
          >
            Bar list
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "pie"}
            className={`investor-portfolio-view-tabs__btn${view === "pie" ? " investor-portfolio-view-tabs__btn--active" : ""}`}
            onClick={() => setView("pie")}
          >
            Pie chart
          </button>
        </div>
      ) : null}

      {!hidePieChart && view === "both" ? (
        <div className="investor-portfolio-visualization investor-portfolio-visualization--both">
          <div className="investor-portfolio-both">
            <PortfolioPieAllocation
              holdings={holdings}
              valueForHolding={valueForHolding}
              formatMoney={formatMoney}
            />
            <div className="investor-portfolio-visualization__bars">
              <h4 className="investor-portfolio-visualization__heading">Holdings</h4>
              <div className="investor-portfolio-bars investor-portfolio-bars--paired">{holdingsList}</div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`investor-portfolio-visualization investor-portfolio-visualization--${hidePieChart ? "bars" : view}`}
        >
          {!hidePieChart && view === "pie" && (
            <div className="investor-portfolio-visualization__chart investor-portfolio-visualization__chart--pie">
              <PortfolioPieAllocation
                holdings={holdings}
                valueForHolding={valueForHolding}
                formatMoney={formatMoney}
              />
            </div>
          )}

          {view === "bars" && (
            <div className="investor-portfolio-visualization__bars">
              <h4 className="investor-portfolio-visualization__heading">Holdings</h4>
              <div className="investor-portfolio-bars">{holdingsList}</div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
